/**
 * EU VAT Field Handler using jsvat library
 * jsvat is a well-maintained, production-ready VAT validation library
 * @version 2.5.4 (latest as of December 2024)
 * @see https://github.com/se-panfilov/jsvat
 */

// Debounce helper function
function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

// Custom element for VAT field with validation
if (!customElements.get('vat-field')) {
  customElements.define(
    'vat-field',
    class VATField extends HTMLElement {
      constructor() {
        super();
        this.countrySelect = this.querySelector('#Cart-vat-country');
        this.numberInput = this.querySelector('#Cart-vat-number');
        this.hiddenInput = this.querySelector('input[name="attributes[VAT-ID]"]');
        this.errorElement = this.querySelector('.vat-field__error');
        this.successElement = this.querySelector('.vat-field__success');
        
        // Wait for jsvat to be loaded (both scripts use defer)
        this.waitForJsvat();
      }

      waitForJsvat() {
        if (window.jsvat && typeof window.jsvat.checkVAT === 'function') {
          this.initValidation();
        } else {
          setTimeout(() => this.waitForJsvat(), 50);
        }
      }

      initValidation() {
        if (!this.countrySelect || !this.numberInput) return;

        // Create debounced validation function once
        const debouncedValidate = debounce(() => this.validateInput(), 500);

        // Auto-uppercase country code
        this.countrySelect.addEventListener('input', (e) => {
          e.target.value = e.target.value.toUpperCase().replace(/[^A-Z]/g, '').slice(0, 2);
          debouncedValidate();
        });
        
        // Handle paste in country field
        this.countrySelect.addEventListener('paste', (e) => {
          e.preventDefault();
          const pastedText = (e.clipboardData || window.clipboardData).getData('text');
          
          if (this.autoParsePaste(pastedText)) {
            this.validateInput();
          } else {
            this.countrySelect.value = pastedText.toUpperCase().replace(/[^A-Z]/g, '').slice(0, 2);
            this.validateInput();
          }
        });
        
        // Handle input in number field
        this.numberInput.addEventListener('input', () => {
          debouncedValidate();
        });
        
        // Handle paste in number field
        this.numberInput.addEventListener('paste', (e) => {
          e.preventDefault();
          const pastedText = (e.clipboardData || window.clipboardData).getData('text');
          
          if (!this.autoParsePaste(pastedText)) {
            this.numberInput.value = pastedText;
          }
          this.validateInput();
        });
        
        // Validate on blur
        this.numberInput.addEventListener('blur', () => this.validateInput());
        this.countrySelect.addEventListener('blur', () => this.validateInput());
        
        // Parse existing value if present
        this.parseExistingValue();
        
        // Validate on page load if fields have values
        if (this.countrySelect.value && this.numberInput.value.trim()) {
          this.validateInput();
        }
      }

      autoParsePaste(value) {
        const fullVATPattern = /^([A-Z]{2})(.+)$/;
        const match = value.trim().toUpperCase().match(fullVATPattern);
        
        if (!match) return false;
        
        const [, country, number] = match;
        
        if (!/\d/.test(number) || number.length <= 3) return false;
        
        this.countrySelect.value = country;
        this.numberInput.value = number;
        return true;
      }

      parseExistingValue() {
        const existingValue = this.hiddenInput?.value?.trim();
        if (!existingValue) return;
        
        const countryMatch = existingValue.match(/^([A-Z]{2})/);
        if (!countryMatch) return;
        
        const country = countryMatch[1];
        const number = existingValue.substring(2);
        this.countrySelect.value = country;
        this.numberInput.value = number;
      }

      validateInput() {
        if (!this.isJsvatAvailable()) return;

        const { country, number } = this.getInputValues();
        
        this.clearMessages();
        
        if (this.isEmptyInput(country, number)) {
          this.handleEmptyInput();
          return;
        }

        const fullVAT = this.combineVAT(country, number);
        const result = this.performValidation(fullVAT);
        
        if (result.isValid) {
          this.handleValidVAT(result, fullVAT);
        } else {
          this.handleInvalidVAT(fullVAT);
        }
      }

      isJsvatAvailable() {
        return window.jsvat && typeof window.jsvat.checkVAT === 'function';
      }

      getInputValues() {
        return {
          country: this.countrySelect.value,
          number: this.numberInput.value.trim()
        };
      }

      isEmptyInput(country, number) {
        return !country || !number;
      }

      handleEmptyInput() {
        this.clearInputValidity();
        this.hiddenInput.value = '';
      }

      clearInputValidity() {
        this.numberInput.setCustomValidity('');
        this.countrySelect.setCustomValidity('');
      }

      combineVAT(country, number) {
        return country + number;
      }

      performValidation(fullVAT) {
        return window.jsvat.checkVAT(fullVAT, window.jsvat.countries);
      }

      handleValidVAT(result, fullVAT) {
        this.showSuccess(result);
        this.clearInputValidity();
        this.hiddenInput.value = result.value || fullVAT;
      }

      handleInvalidVAT(fullVAT) {
        this.showError(fullVAT);
        this.numberInput.setCustomValidity(this.getErrorMessage(fullVAT));
        this.hiddenInput.value = fullVAT;
      }

      showSuccess(result) {
        const message = this.getSuccessMessage(result);
        this.displaySuccessMessage(message);
        this.setValidationState('valid');
      }

      showError(value) {
        const message = this.getErrorMessage(value);
        this.displayErrorMessage(message);
        this.setValidationState('error');
      }

      clearMessages() {
        this.hideMessages();
        this.setValidationState('neutral');
      }

      getSuccessMessage(result) {
        const countryName = result.country?.name || result.country?.isoCode?.short || 'EU';
        return window.vatStrings?.success?.replace('[country]', countryName) 
          || `✓ Valid ${countryName} VAT number`;
      }

      displaySuccessMessage(message) {
        if (!this.successElement) return;
        
        this.successElement.textContent = message;
        this.successElement.classList.remove('hidden');
      }

      displayErrorMessage(message) {
        if (!this.errorElement) return;
        
        this.errorElement.textContent = message;
        this.errorElement.classList.remove('hidden');
      }

      hideMessages() {
        if (this.errorElement) this.errorElement.classList.add('hidden');
        if (this.successElement) this.successElement.classList.add('hidden');
      }

      setValidationState(state) {
        this.classList.remove('vat-field--valid', 'vat-field--error');
        
        if (state === 'valid') {
          this.classList.add('vat-field--valid');
        } else if (state === 'error') {
          this.classList.add('vat-field--error');
        }
      }

      getErrorMessage(value) {
        const countryCode = this.extractCountryCode(value);
        
        if (this.isValidCountryCodeFormat(countryCode) && !this.isEUCountry(countryCode)) {
          return this.getInvalidCountryMessage(countryCode);
        }
        
        return this.getInvalidFormatMessage();
      }

      extractCountryCode(value) {
        const normalized = value.toUpperCase().replace(/[\s\-\.]/g, '');
        return normalized.substring(0, 2);
      }

      isValidCountryCodeFormat(code) {
        return /^[A-Z]{2}/.test(code);
      }

      isEUCountry(code) {
        const euCountries = [
          'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR',
          'DE', 'EL', 'GR', 'HU', 'IE', 'IT', 'LV', 'LT', 'LU', 'MT',
          'NL', 'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE'
        ];
        return euCountries.includes(code);
      }

      getInvalidCountryMessage(code) {
        const strings = window.vatStrings || {};
        return strings.invalid_country?.replace('[code]', code) 
          || `Country code '${code}' is not a valid EU country`;
      }

      getInvalidFormatMessage() {
        const strings = window.vatStrings || {};
        return strings.invalid_format || 'Invalid VAT number format. Please check and try again.';
      }
    }
  );
}